/**
 * Duplex speech streaming for the article reader: fetch the authenticated
 * `/speech/stream` endpoint and turn the live provider audio (Gemini by
 * default) into a playable object URL — progressively via MediaSource where
 * supported, or buffered via a Blob (iOS WKWebView / Safari, which lack MSE
 * audio). No GCS write, no task, no polling.
 */
import { apiBaseUrl, type Api } from "../../../services/api";
import { getFirebaseToken } from "../../../services/firebase";

/** One synthesis streamed from the API, and whether all of it arrived. */
type SpeechStream = {
  articleId: string;
  controller: AbortController;
  finished: boolean;
};

// The object URL minted by the previous play, and the stream behind it. We
// revoke it when a new one is created (never the *current* one — that would
// break in-flight MediaSource playback), so at most one dangling URL exists.
let previousObjectUrl: string | null = null;
let previousObjectUrlStream: SpeechStream | null = null;

function rememberObjectUrl(objectUrl: string, stream: SpeechStream): string {
  if (previousObjectUrl !== null) {
    URL.revokeObjectURL(previousObjectUrl);
  }
  previousObjectUrl = objectUrl;
  previousObjectUrlStream = stream;
  return objectUrl;
}

// The latest stream. Starting a new stream (a new listen, or switching
// article) aborts it while it still runs — cancelling its fetch, reader, and
// the provider synthesis behind it — so at most one stream runs at a time.
let currentStream: SpeechStream | null = null;

/**
 * Stops `articleId`'s stream while it still runs: a request the reader has
 * abandoned, whose synthesis would otherwise go on being billed.
 */
export function cancelSpeechStream(articleId: string): void {
  if (
    currentStream === null ||
    currentStream.articleId !== articleId ||
    currentStream.finished
  ) {
    return;
  }
  currentStream.controller.abort();
}

/**
 * Whether a track URL can still play in full. A persisted file always can. An
 * object URL from this module cannot once a later one replaced (revoked) it,
 * or once a later stream cut its own stream off before it finished.
 */
export function isSpeechUrlIntact(url: string): boolean {
  if (!url.startsWith("blob:")) return true;
  if (url !== previousObjectUrl || previousObjectUrlStream === null) {
    return false;
  }
  return (
    previousObjectUrlStream.finished ||
    !previousObjectUrlStream.controller.signal.aborted
  );
}

function mediaSourceSupportsMp3(): boolean {
  return (
    typeof MediaSource !== "undefined" &&
    MediaSource.isTypeSupported("audio/mpeg")
  );
}

/** Append one chunk, resolving only once the SourceBuffer finishes (no overlap). */
function appendChunk(
  sourceBuffer: SourceBuffer,
  chunk: Uint8Array
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const cleanup = (): void => {
      sourceBuffer.removeEventListener("updateend", onUpdateEnd);
      sourceBuffer.removeEventListener("error", onError);
    };
    const onUpdateEnd = (): void => {
      cleanup();
      resolve();
    };
    const onError = (): void => {
      cleanup();
      reject(new Error("MediaSource SourceBuffer append failed"));
    };
    sourceBuffer.addEventListener("updateend", onUpdateEnd);
    sourceBuffer.addEventListener("error", onError);
    // Copy into a fresh ArrayBuffer-backed view: the stream reader yields
    // `Uint8Array<ArrayBufferLike>` (possibly SharedArrayBuffer), which
    // `appendBuffer` (BufferSource over ArrayBuffer) does not accept.
    sourceBuffer.appendBuffer(new Uint8Array(chunk));
  });
}

async function pumpToMediaSource(
  mediaSource: MediaSource,
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  stream: SpeechStream
): Promise<void> {
  const sourceBuffer = mediaSource.addSourceBuffer("audio/mpeg");
  const reader = body.getReader();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      await appendChunk(sourceBuffer, value);
    }
    stream.finished = true;
    if (mediaSource.readyState === "open") {
      mediaSource.endOfStream();
    }
  } catch (error) {
    // Release the network resource immediately (cancel the fetch body) instead
    // of leaving the provider stream + Cloud Run request flowing until GC —
    // whether we failed to append (audio element gone) or were aborted.
    void reader.cancel().catch(() => {
      // Reader already errored/cancelled — nothing to release.
    });
    // An abort (new listen / article switch) is expected; only a real failure
    // is worth a warning. This runs detached from the fetch (via `void`), so it
    // can't change the response — the <audio> element just ends the clip.
    if (!signal.aborted) {
      console.warn("speech stream pump failed", error);
    }
    if (mediaSource.readyState === "open") {
      try {
        mediaSource.endOfStream();
      } catch {
        // MediaSource already torn down — nothing to end.
      }
    }
  }
}

function mediaSourceObjectUrl(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
  stream: SpeechStream
): string {
  const mediaSource = new MediaSource();
  const objectUrl = URL.createObjectURL(mediaSource);
  mediaSource.addEventListener(
    "sourceopen",
    () => {
      void pumpToMediaSource(mediaSource, body, signal, stream);
    },
    { once: true }
  );
  return objectUrl;
}

/**
 * Fetch the article's streamed speech and return an object URL the audio player
 * can use as a plain `src`. Throws on auth failure, empty content (204), or a
 * non-OK status so the caller can toast + abort.
 */
type SpeechStreamQuery =
  Api["get"]["/webapp/private/articles/:id/speech/stream"]["input"]["query"];

/**
 * The authenticated endpoint that synthesizes and streams an article's audio.
 * `stream_id` names the file the API saves the stream to, so the player can
 * switch to that same synthesis once it is whole. Built by hand because the
 * native shell fetches it, but the query is typed from the route.
 */
export function speechStreamUrl(
  articleId: string,
  query: SpeechStreamQuery
): string {
  return `${apiBaseUrl}/webapp/private/articles/${articleId}/speech/stream?${new URLSearchParams(query).toString()}`;
}

export async function streamSpeechToObjectUrl(
  articleId: string
): Promise<string> {
  // Cancel any prior in-flight stream so a new listen / article switch never
  // leaves an orphaned fetch + provider synthesis running in the background.
  if (currentStream !== null && !currentStream.finished) {
    currentStream.controller.abort();
  }
  const abortController = new AbortController();
  const stream: SpeechStream = {
    articleId,
    controller: abortController,
    finished: false,
  };
  currentStream = stream;
  const { signal } = abortController;

  const token = await getFirebaseToken();
  if (token === null) {
    throw new Error("Not authenticated for speech streaming");
  }

  const response = await fetch(
    speechStreamUrl(articleId, { stream_id: crypto.randomUUID() }),
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    }
  );
  if (response.status === 204) {
    throw new Error("Article has no audio content to synthesize");
  }
  if (!response.ok) {
    throw new Error(`Speech stream failed: ${String(response.status)}`);
  }

  const body = response.body;
  if (mediaSourceSupportsMp3() && body !== null) {
    return rememberObjectUrl(
      mediaSourceObjectUrl(body, signal, stream),
      stream
    );
  }

  // Fallback (iOS/Safari): buffer the whole stream, then play — still no
  // GCS write and no polling, just no progressive start. `signal` on the fetch
  // means an abort rejects this read too.
  const blob = await response.blob();
  stream.finished = true;
  return rememberObjectUrl(URL.createObjectURL(blob), stream);
}
